/**
 * The Requests page's two views: what is waiting on you, and what is waiting on anyone. "Yours"
 * means you take the session, or you host the event the seat is for.
 */

export const REQUEST_VIEWS = ["mine", "all"] as const;
export type RequestView = (typeof REQUEST_VIEWS)[number];

export interface OwnedRequest {
  specialistId: string;
  eventId: string | null;
}

/** Hosts per event id, from the events list. Seats of an event nobody looked up fall back to the specialist. */
export type EventHosts = ReadonlyMap<string, readonly string[]>;

export const isMine = (request: OwnedRequest, userId: string, eventHosts: EventHosts) => {
  if (request.specialistId === userId) return true;
  return request.eventId ? (eventHosts.get(request.eventId)?.includes(userId) ?? false) : false;
};

export const requestsInView = <Row extends OwnedRequest>(
  rows: Row[],
  { view, userId, eventHosts }: { view: RequestView; userId: string; eventHosts: EventHosts },
) => (view === "all" ? rows : rows.filter((row) => isMine(row, userId, eventHosts)));

/**
 * Where the page opens when the URL does not say. Someone who takes bookings starts on their own
 * queue, unless it is empty while others wait: nobody should land on "nothing for you" with work
 * sitting one tab over. The front desk takes nobody's sessions, so it starts on everything.
 */
export const defaultRequestView = ({
  isSpecialist,
  mine,
  all,
}: {
  isSpecialist: boolean;
  mine: number;
  all: number;
}): RequestView => (isSpecialist && (mine > 0 || all === 0) ? "mine" : "all");

const HOUR_MS = 3_600_000;
const MAX_RANGE_MS = 100 * 24 * HOUR_MS;

/**
 * The window the events list has to cover to find every event a seat request points at. The
 * API caps a range at 100 days; seats beyond that fall back to the specialist on the booking.
 */
export const eventSeatRange = (requests: (OwnedRequest & { startAt: string })[]) => {
  const starts = requests
    .filter((request) => request.eventId)
    .map((request) => new Date(request.startAt).getTime())
    .toSorted((a, b) => a - b);
  if (starts.length === 0) return null;
  const from = starts[0] - HOUR_MS;
  const to = Math.min((starts.at(-1) ?? from) + 24 * HOUR_MS, from + MAX_RANGE_MS);
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
};
